<template>
    <div v-if="isActive" ref="magnifierRef" class="tenjityModule-magnifier"
        :class="{ 'tenjityModule-magnifier-minimal': !frameVisible, 'tenjityModule-magnifier-draggable': mode === 'zone' }"
        :style="magnifierStyle" @mousedown="startDrag" aria-hidden="true">
        <canvas ref="magnifierCanvas" class="tenjityModule-magnifier-canvas"></canvas>
    </div>
</template>

<script>
import html2canvas from "html2canvas";
import {
    normalizeZoom,
    normalizeSize,
    normalizeCoordinate,
    calculateLensPosition,
    calculateSourceRegion,
    validateMode,
    getInitialPosition,
    MAGNIFIER_DEFAULTS,
} from "../composables/useMagnifier.js";

export default {
    name: "MagnifierView",
    props: {
        isActive: { type: Boolean, required: true },
        zoom: { type: Number, required: true },
        size: { type: Number, required: true },
        mode: { type: String, required: true },
        frameVisible: { type: Boolean, default: true },
        fixedX: { type: Number, required: true },
        fixedY: { type: Number, required: true },
        cursorPosition: { type: Object, required: true },
        moduleZoomValue: { type: Number, default: 100 },
        pageZoomValue: { type: Number, default: 100 },
        zoomWrapper: { type: String, default: "#zoomWrapper" },
    },
    data() {
        return {
            lensPosition: { x: 0, y: 0 },
            snapshotCanvas: null,
            snapshotScaleX: 1,
            snapshotScaleY: 1,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
            isCapturing: false,
            dragOffset: { x: 0, y: 0 },
            isDragging: false,
            refreshTimer: null,
            rafId: null,
            domMutationObserver: null,
            _moduleContentEl: null,
        };
    },
    computed: {
        magnifierStyle() {
            const size = normalizeSize(this.size);
            return {
                width: `${size}px`,
                height: `${size}px`,
                left: `${this.lensPosition.x}px`,
                top: `${this.lensPosition.y}px`,
            };
        }
    },
    watch: {
        isActive(newVal) {
            if (newVal) {
                this.initializeListeners();
                this.startDomObservation();
                this.captureSnapshot();
            } else {
                this.cleanupListeners();
                this.stopDomObservation();
            }
        },
        zoom() {
            // Le zoom loupe ne change pas le contenu, juste le rendu → drawCanvas suffit
            this.drawCanvas();
        },
        size() {
            if (this.isActive) {
                this.updatePosition();
                if (this.snapshotCanvas) this.drawCanvas();
            }
        },
        mode() {
            if (this.isActive) {
                this.updatePosition();
                if (this.snapshotCanvas) this.drawCanvas();
            }
        },
        fixedX() {
            if (this.isActive && this.mode === "zone") {
                this.updatePosition();
                if (this.snapshotCanvas) this.drawCanvas();
            }
        },
        fixedY() {
            if (this.isActive && this.mode === "zone") {
                this.updatePosition();
                if (this.snapshotCanvas) this.drawCanvas();
            }
        },
        cursorPosition() {
            if (this.isActive && this.mode === "cursor") {
                this.updatePosition();
                if (this.snapshotCanvas) this.drawCanvas();
            }
        },
        // Quand le zoom du module change (prop passée depuis App.vue),
        // recapturer car le rendu visuel de la page a changé
        moduleZoomValue() {
            if (this.isActive) this.scheduleCapture(120);
        },
        // Quand le zoom page change, html2canvas ne capture pas le CSS zoom →
        // on redessine immédiatement avec le nouveau facteur de compensation
        pageZoomValue() {
            if (this.isActive && this.snapshotCanvas) this.drawCanvas();
        },
    },
    methods: {
        // ─── Position ────────────────────────────────────────────────────────────
        updatePosition() {
            const size = normalizeSize(this.size);
            let centerX, centerY;
            if (this.mode === "zone" || this.mode === "pinned") {
                centerX = this.fixedX;
                centerY = this.fixedY;
            } else {
                centerX = this.cursorPosition.x;
                centerY = this.cursorPosition.y;
            }
            this.lensPosition = calculateLensPosition(centerX, centerY, size);
        },

        // ─── Capture ─────────────────────────────────────────────────────────────
        scheduleCapture(delay = 30) {
            // Annule toute capture en attente et planifie la suivante.
            // Si une capture est déjà en cours (isCapturing), elle se terminera
            // et drawCanvas sera appelé → le snapshot précédent reste affiché
            // entretemps, ce qui est acceptable.
            if (this.refreshTimer) clearTimeout(this.refreshTimer);
            this.refreshTimer = setTimeout(() => {
                this.refreshTimer = null;
                // Attendre la fin des transitions CSS avant de capturer
                const running = document.getAnimations?.()?.filter(a => a.playState === 'running') ?? [];
                if (running.length > 0) {
                    const fallback = setTimeout(() => this.captureSnapshot(), 80);
                    Promise.allSettled(running.map(a => a.finished)).then(() => {
                        clearTimeout(fallback);
                        this.captureSnapshot();
                    });
                } else {
                    this.captureSnapshot();
                }
            }, delay);
        },

        // Request an immediate draw on next animation frame (debounced)
        requestDraw() {
            if (this.rafId) cancelAnimationFrame(this.rafId);
            this.rafId = requestAnimationFrame(() => {
                this.rafId = null;
                this.drawCanvas();
            });
        },

        async captureSnapshot() {
            if (this.isCapturing) return;
            this.isCapturing = true;
            try {
                const element = document.documentElement;

                // On capture exactement ce qui est visible dans le viewport.
                // x/y: scrollX/Y décale la fenêtre de capture pour ignorer le
                // contenu hors-écran en haut/gauche (important quand la page est scrollée).
                // width/height: innerWidth/Height cadre sur le viewport, pas sur
                // tout le document (évite de capturer du contenu hors-écran droit/bas).
                this.snapshotCanvas = await html2canvas(element, {
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: null,
                    scale: window.devicePixelRatio || 1,
                    logging: false,
                    x: window.scrollX,
                    y: window.scrollY,
                    width: window.innerWidth,
                    height: window.innerHeight,
                    ignoreElements: (el) => el.classList.contains('tenjityModule-magnifier'),
                });

                // Le canvas produit représente exactement le viewport → scale = devicePixelRatio
                this.snapshotScaleX = this.snapshotCanvas.width / window.innerWidth;
                this.snapshotScaleY = this.snapshotCanvas.height / window.innerHeight;

                this.$nextTick(() => { this.requestDraw(); });
            } catch (error) {
                console.error("Erreur lors de la capture magnifier:", error);
            } finally {
                this.isCapturing = false;
            }
        },

        // ─── Rendu canvas ────────────────────────────────────────────────────────
        drawCanvas() {
            if (!this.$refs.magnifierCanvas || !this.snapshotCanvas) return;
            const ctx = this.$refs.magnifierCanvas.getContext("2d");
            if (!ctx) return;

            const size = normalizeSize(this.size);
            this.$refs.magnifierCanvas.width = size;
            this.$refs.magnifierCanvas.height = size;

            // html2canvas ne capture pas le CSS zoom de #zoomWrapper → on compense
            // en multipliant le zoom loupe par le facteur de zoom page, pour que le
            // texte dans la loupe reste plus grand que le texte sur la page.
            const zoom = normalizeZoom(this.zoom) * (this.pageZoomValue / 100);

            // Le snapshot est déjà dans l'espace viewport (x/y scroll compensé lors
            // de la capture) → on ne passe plus scrollX/Y ici pour éviter le double
            // compensation qui causait le décalage.
            const { srcX, srcY, srcWidth, srcHeight } = calculateSourceRegion(
                this.lensPosition.x,
                this.lensPosition.y,
                size,
                zoom,
                this.snapshotCanvas.width,
                this.snapshotCanvas.height,
                this.snapshotScaleX,
                this.snapshotScaleY,
            );

            if (srcWidth <= 0 || srcHeight <= 0) return;
            ctx.drawImage(this.snapshotCanvas, srcX, srcY, srcWidth, srcHeight, 0, 0, size, size);
        },

        // ─── Drag (mode zone) ────────────────────────────────────────────────────
        startDrag(event) {
            if (this.mode !== "zone" || event.button !== 0) return;
            this.isDragging = true;
            const size = normalizeSize(this.size);
            this.dragOffset = {
                x: event.clientX - this.lensPosition.x - size / 2,
                y: event.clientY - this.lensPosition.y - size / 2,
            };
            const handleMouseMove = (e) => this.continueDrag(e);
            const handleMouseUp = () => this.endDrag(handleMouseMove);
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp, { once: true });
        },
        continueDrag(event) {
            if (!this.isDragging) return;
            const newX = event.clientX - this.dragOffset.x;
            const newY = event.clientY - this.dragOffset.y;
            this.$emit("update:fixedX", normalizeCoordinate(newX, "x", normalizeSize(this.size), this.viewportWidth, this.viewportHeight));
            this.$emit("update:fixedY", normalizeCoordinate(newY, "y", normalizeSize(this.size), this.viewportWidth, this.viewportHeight));
            this.updatePosition();
            this.drawCanvas();
        },
        endDrag(handleMouseMove) {
            this.isDragging = false;
            document.removeEventListener("mousemove", handleMouseMove);
        },

        // ─── Handlers d'événements ───────────────────────────────────────────────
        handleMouseMove(event) {
            if (this.mode === "cursor") {
                this.$emit("update:cursorPosition", { x: event.clientX, y: event.clientY });
                this.updatePosition();
                this.requestDraw();
            }
        },
        handleTouchStart(event) {
            if (!this.isActive || !event.touches?.length) return;
            const touch = event.touches[0];
            if (this.mode === "cursor") {
                this.$emit("update:cursorPosition", { x: touch.clientX, y: touch.clientY });
                this.updatePosition();
                this.requestDraw();
            }
        },
        handleTouchMove(event) {
            if (!this.isActive || !event.touches?.length) return;
            const touch = event.touches[0];
            if (this.mode === "cursor") {
                this.$emit("update:cursorPosition", { x: touch.clientX, y: touch.clientY });
                this.updatePosition();
                this.requestDraw();
            }
            event.preventDefault();
        },
        handleScroll() {
            if (!this.isActive) return;
            // Redessiner immédiatement avec le snapshot existant (position inchangée)
            // puis recapturer rapidement pour actualiser le contenu
            this.updatePosition();
            if (this.snapshotCanvas) this.requestDraw();
            this.scheduleCapture(40);
        },
        handleModuleScroll() {
            if (!this.isActive) return;
            // Le contenu du module a bougé → recapturer rapidement
            this.scheduleCapture(30);
        },
        handleInputInteraction(event) {
            if (!this.isActive) return;
            // Sliders : recapture fréquente pendant le glissement
            // Autres inputs/toggles : délai standard
            const isRange = event.target?.type === "range";
            this.scheduleCapture(isRange ? 16 : 30);
        },
        handleDocumentInteraction() {
            if (!this.isActive) return;
            this.scheduleCapture(30);
        },
        handleResize() {
            if (!this.isActive) return;
            this.viewportWidth = window.innerWidth;
            this.viewportHeight = window.innerHeight;
            this.scheduleCapture(100);
        },

        // ─── Listeners souris/tactile/scroll ────────────────────────────────────
        initializeListeners() {
            document.addEventListener("mousemove", this.handleMouseMove);
            document.addEventListener("touchstart", this.handleTouchStart);
            document.addEventListener("touchmove", this.handleTouchMove, { passive: false });
            window.addEventListener("scroll", this.handleScroll);
            window.addEventListener("resize", this.handleResize);
        },
        cleanupListeners() {
            document.removeEventListener("mousemove", this.handleMouseMove);
            document.removeEventListener("touchstart", this.handleTouchStart);
            document.removeEventListener("touchmove", this.handleTouchMove);
            window.removeEventListener("scroll", this.handleScroll);
            window.removeEventListener("resize", this.handleResize);
            if (this.refreshTimer) {
                clearTimeout(this.refreshTimer);
                this.refreshTimer = null;
            }
            if (this.rafId) {
                cancelAnimationFrame(this.rafId);
                this.rafId = null;
            }
        },

        // ─── Observation DOM (changements de contenu) ────────────────────────────
        startDomObservation() {
            if (this.domMutationObserver || !document.body) return;

            this.domMutationObserver = new MutationObserver(() => {
                if (this.isActive) this.scheduleCapture(40);
            });
            this.domMutationObserver.observe(document.body, {
                subtree: true,
                childList: true,
                attributes: true,
                characterData: false,
                attributeFilter: ["class", "style", "aria-expanded", "aria-pressed", "aria-checked"],
            });

            // Interactions générales (clicks, toggles)
            document.addEventListener("click", this.handleDocumentInteraction, true);
            document.addEventListener("pointerup", this.handleDocumentInteraction, true);

            // Sliders et inputs : recapture pendant le drag, pas seulement au relâché
            document.addEventListener("input", this.handleInputInteraction, true);
            document.addEventListener("change", this.handleInputInteraction, true);

            // Scroll interne du module (overflow: auto sur .tenjityModule-content)
            // Attaché ici et pas dans initializeListeners pour éviter le doublon
            this._moduleContentEl = document.querySelector(".tenjityModule-content");
            if (this._moduleContentEl) {
                this._moduleContentEl.addEventListener("scroll", this.handleModuleScroll);
            }
        },
        stopDomObservation() {
            if (this.domMutationObserver) {
                this.domMutationObserver.disconnect();
                this.domMutationObserver = null;
            }
            document.removeEventListener("click", this.handleDocumentInteraction, true);
            document.removeEventListener("pointerup", this.handleDocumentInteraction, true);
            document.removeEventListener("input", this.handleInputInteraction, true);
            document.removeEventListener("change", this.handleInputInteraction, true);
            if (this._moduleContentEl) {
                this._moduleContentEl.removeEventListener("scroll", this.handleModuleScroll);
                this._moduleContentEl = null;
            }
        },

        // ─── API publique (accès depuis le parent via ref) ───────────────────────
        captureSnapshotPublic() {
            return this.captureSnapshot();
        },
        getCanvas() { return this.$refs.magnifierCanvas; },
        getMagnifierRef() { return this.$refs.magnifierRef; },
        getLensPosition() { return this.lensPosition; },
        getSnapshotCanvas() { return this.snapshotCanvas; },
        normalizeZoom(value) { return normalizeZoom(value); },
        normalizeSize(value) { return normalizeSize(value); },
        normalizeCoordinate(value, axis, size, viewportWidth, viewportHeight) {
            return normalizeCoordinate(value, axis, size, viewportWidth, viewportHeight);
        },
        validateMode(mode) { return validateMode(mode); },
        getInitialPosition() { return getInitialPosition(); },
        getMagnifierDefaults() { return MAGNIFIER_DEFAULTS; },
    },
    mounted() {
        if (this.isActive) {
            this.$nextTick(() => {
                this.updatePosition();
                this.initializeListeners();
                this.startDomObservation();
                this.captureSnapshot();
            });
        }
    },
    beforeUnmount() {
        this.cleanupListeners();
        this.stopDomObservation();
    }
};
</script>